import { useState } from "react";
import { History, ChevronDown, Lock } from "lucide-react";
import { niceName, fmtDate } from "@/lib/format";

export const DECISION_TYPES = new Set(["check_resolve", "check_reopen", "status", "status_change", "process_update", "issue_date", "conflict", "converted"]);
const MERGE_LABEL = {
  ai_confirm: "documentos revistos", upload: "ficheiros carregados", ai_read: "documentos lidos",
  status: "estados alterados", portal_send: "envios da página do cliente", issue_date: "datas de emissão definidas",
  check_resolve: "avisos resolvidos",
};
const time = (iso) => new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
const dayKey = (iso) => new Date(iso).toLocaleDateString("sv-SE");
const dayLabel = (iso) => {
  const k = dayKey(iso), t = new Date();
  if (k === t.toLocaleDateString("sv-SE")) return "Hoje";
  if (k === new Date(t.getTime() - 864e5).toLocaleDateString("sv-SE")) return "Ontem";
  return fmtDate(k);
};
const fmtVal = (v) => (/^\d{4}-\d{2}-\d{2}/.test(String(v || "")) ? fmtDate(v) : v || "—");

function mergeRuns(events) {
  const out = [];
  events.forEach((e) => {
    const last = out[out.length - 1];
    const mergeable = DECISION_TYPES.has(e.type) ? (MERGE_LABEL[e.type] && !e.detail && e.before == null) : true;
    if (last && mergeable && last.type === e.type && last.actor_name === e.actor_name && last.mergeable && (e.type !== "media" || last.title === e.title)) last.items.push(e);
    else out.push({ ...e, mergeable, items: [e] });
  });
  return out;
}

function Event({ g, decision }) {
  const who = niceName(g.actor_name) || "Sistema";
  const many = g.items.length > 1;
  return (
    <div className="py-2.5 flex gap-3" data-testid={`history-event-${g.type}`}>
      <span className="font-mono text-[11px] text-slate-400 w-10 shrink-0 pt-0.5">{time(g.created_at)}</span>
      <div className="min-w-0 text-sm">
        <div className="text-ec-green">
          {many ? <><b>{g.items.length} {MERGE_LABEL[g.type] || `× ${g.title}`}</b> por {who}</> : <>{g.title} <span className="text-slate-400">· {who}</span></>}
        </div>
        {many && new Set(g.items.map((x) => x.title)).size > 1 && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{[...new Set(g.items.map((x) => x.title.split(":")[0].replace(/[“”]/g, "")))].join(" · ")}</div>}
        {!many && g.detail && <div className={`text-xs mt-0.5 ${decision ? "text-slate-700" : "text-slate-500"}`}>{decision && g.type === "check_resolve" ? "Motivo: " : ""}{g.detail}</div>}
        {!many && decision && g.type === "check_resolve" && !g.detail && <div className="text-xs mt-0.5 text-slate-400">Sem motivo escrito</div>}
        {!many && (g.before != null || g.after != null) && (
          <div className="text-xs mt-0.5 text-slate-600" data-testid="history-before-after">
            Antes: <span className="line-through text-slate-400">{fmtVal(g.before)}</span> · Depois: <b>{fmtVal(g.after)}</b>
          </div>
        )}
      </div>
    </div>
  );
}

export function HistoryPanel({ events, defaultOpen = false, bare = false, initialFilter = "decisoes" }) {
  const [open, setOpen] = useState(defaultOpen);
  const [filter, setFilter] = useState(initialFilter);
  const last = events[0];
  const list = filter === "decisoes" ? events.filter((e) => DECISION_TYPES.has(e.type)) : events;
  const days = [];
  list.forEach((e) => {
    const k = dayKey(e.created_at);
    if (!days.length || days[days.length - 1].k !== k) days.push({ k, label: dayLabel(e.created_at), items: [] });
    days[days.length - 1].items.push(e);
  });
  return (
    <div className={bare ? "" : "bg-white rounded-[16px] px-6 py-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100"} data-testid={bare ? "history-tab" : "history-panel"}>
      {!bare && <button type="button" onClick={() => setOpen(!open)} data-testid="history-toggle" className="w-full flex items-center gap-2 text-left">
        <History size={17} strokeWidth={1.5} className="text-ec-gold shrink-0" />
        <span className="text-sm text-ec-green truncate">
          <b>Histórico</b> ({events.length} acontecimentos){last && <> · último: {dayLabel(last.created_at).toLowerCase()}, {time(last.created_at)}, {niceName(last.actor_name) || "Sistema"}</>}
        </span>
        <ChevronDown size={16} className={`ml-auto text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>}
      {open && (
        <div className={bare ? "" : "mt-4"}>
          <div className="flex items-center gap-2 mb-3">
            {[["decisoes", "Decisões"], ["tudo", "Tudo"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`history-filter-${v}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === v ? "bg-ec-green text-white border-ec-green" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-400"><Lock size={11} /> Não pode ser apagado nem editado</span>
          </div>
          {days.length === 0 && <p className="text-sm text-slate-400">Sem {filter === "decisoes" ? "decisões" : "registos"}.</p>}
          {days.map((d) => (
            <div key={d.k} className="mb-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100 pb-1">{d.label}</div>
              <div className="divide-y divide-slate-50">
                {mergeRuns(d.items).map((g) => <Event key={g.id} g={g} decision={filter === "decisoes" || DECISION_TYPES.has(g.type)} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
